import { EventEmitter } from 'events';
import { generateUniqueHash } from './Hash/generator';
import IMessage from './IMessage';
import Video from './Video/Video';
import Stream from './Stream/Stream';
import Offline from './Offline/Offline';
import ILoadFileController from './Offline/LoadFile/ILoadFileController';
import JavascriptLoadFileController from './Offline/LoadFile/JavascriptLoadFileController';
import Command from './Command/Command';
import Hardware from './Hardware/Hardware';
import Browser from './Browser/Browser';
import Iframe from './Iframe/Iframe';
import Touch from './Touch/Touch';
import Input from './Input/Input';
import Debug from './Debug/Debug';
import DeviceInfo from './DeviceInfo/DeviceInfo';
import Display from './Display/Display';
import Timing from './Timing/Timing';
import Sync from './Sync/Sync';
import FileSystem from './FileSystem/FileSystem';
import { createError, ISosErrorTransferObject } from './Error/errorHelper';
import Management from './Management/Management';
import Monitors from './Monitors/Monitors';
import Sensors from './Sensors/Sensors';
import { createExchange } from './Exchange/Exchange';
import OSD from './OSD/OSD';
import ProofOfPlay from './ProofOfPlay/ProofOfPlay';
import NativeCommands from './NativeCommands/NativeCommands';
import { WeinreData } from './Debug/IDebug';
import Connect from './Connect/Connect';

/**
 * The `sos` API groups together all functionality the \@signageos/front-applet offers. This is a basic object that provides you with access to
 * all the applet functionality, such as device information, display control, video playback, and more.
 *
 * :::warning
 * All calls to the sos object need to happen after `sos.onReady()` is resolved. After the sOS object is ready, there are global properties available, which can be used for applet customization and device identification.
 * :::
 *
 * @example
 * // Simple example of using the sos API to play a video.
 * import sos from '@signageos/front-applet';
 *
 * sos.onReady().then(async function () {
 *   startYourApplet(); // Example method to run
 *   // Any other code
 * }
 */
export default class FrontApplet {
	/**
	 * The `config` property is a Key-value dictionary of the applet configuration. It is assigned on the device with timing via Box or SDK/Rest API.
	 *
	 * @since 1.5.0
	 * @example // {@link https://developers.signageos.io/docs/sos-guides/configuration/ | How to use configuration}
	 */
	public readonly config: Record<string, number | string | boolean> = undefined!;

	/**
	 * The `authHash` property is an alternative device identifier, which is designed for secure pairing and locating devices through
	 * the REST API.
	 *
	 * 1. The `authHash` is commonly used as a link between the Applet deployed on the device and the device in device management.
	 * 2. The `authHash` is unique and can be revoked upon request.
	 * 3. The `authHash` can be used as a universal identifier for your Applet application.
	 *
	 * @since 1.0.0
	 * @tutorial https://developers.signageos.io/docs/sos-guides/device-identification
	 */
	public readonly authHash: string = undefined!;

	/**
	 * The `appletVersion` is the version of the current applet.
	 *
	 * @since 4.5.0
	 * @example
	 * sos.onReady().then(async function () {
	 *  console.log(sos.appletVersion); // e.g. "4.5.0"
	 * });
	 */
	public readonly appletVersion: string = undefined!;

	// volatile, set if the FrontApplet is running inside an iframe
	public readonly apiJsUri: string | undefined;

	public readonly debug: Debug;
	public readonly deviceInfo: DeviceInfo;
	public readonly display: Display;
	public readonly stream: Stream;
	public readonly video: Video;
	public readonly command: Command;
	public readonly hardware: Hardware;
	public readonly offline: Offline;
	public readonly browser: Browser;
	public readonly iframe: Iframe;
	public readonly touch: Touch;
	public readonly input: Input;
	public readonly timing: Timing;
	public readonly sync: Sync;
	public readonly fileSystem: FileSystem;
	public readonly management: Management;
	public readonly monitors: Monitors;
	public readonly osd: OSD;
	public readonly proofOfPlay: ProofOfPlay;
	public readonly sensors: Sensors;
	public readonly native: NativeCommands;
	public readonly connect: Connect;

	private readyEmitter: EventEmitter;
	private invocationResponseEmitter: EventEmitter;
	private javascriptLoadFileController: ILoadFileController;

	/** @internal */
	constructor(
		public readonly window: Window & WeinreData,
		private messagePrefix: string,
	) {
		const rootExchange = createExchange(messagePrefix, this.postMessage.bind(this));

		this.readyEmitter = new EventEmitter();
		this.invocationResponseEmitter = new EventEmitter();
		this.debug = new Debug(this.window, messagePrefix, this.postMessage.bind(this));
		this.deviceInfo = new DeviceInfo(messagePrefix, this.postMessage.bind(this));
		this.display = new Display(messagePrefix, this.postMessage.bind(this));
		this.stream = new Stream(messagePrefix, this.postMessage.bind(this));
		this.video = new Video(messagePrefix, this.postMessage.bind(this));
		this.command = new Command(this.messagePrefix, this.postMessage.bind(this));
		this.hardware = new Hardware(this.messagePrefix, this.postMessage.bind(this));
		this.offline = new Offline(messagePrefix, this.window, this.postMessage.bind(this));
		this.browser = new Browser(rootExchange.createNamespaced('browser'));
		this.touch = new Touch(this.window, this.messagePrefix, this.postMessage.bind(this));
		this.iframe = new Iframe(this.window, this.messagePrefix, this, [this.touch.transformIframeData.bind(this.touch)]);
		this.input = new Input(this.window, this.messagePrefix);
		this.timing = new Timing(this.messagePrefix, this.postMessage.bind(this));
		this.sync = new Sync(this.messagePrefix, this.postMessage.bind(this));
		this.fileSystem = new FileSystem(this.messagePrefix, this.postMessage.bind(this));
		this.management = new Management(messagePrefix, this.postMessage.bind(this));
		this.monitors = new Monitors(messagePrefix, this.postMessage.bind(this));
		this.osd = new OSD(messagePrefix, this.postMessage.bind(this));
		this.proofOfPlay = new ProofOfPlay(this.command);
		this.sensors = new Sensors(messagePrefix);
		this.native = new NativeCommands(messagePrefix, this.postMessage.bind(this));
		this.connect = new Connect(this.window, this.messagePrefix);
		this.javascriptLoadFileController = new JavascriptLoadFileController(this.window);
		this.window.addEventListener('message', (event: MessageEvent) => this.handleMessage(event));
		void this.postMessage({ type: messagePrefix + '_api.ready' });
		this.startPinging();
	}

	/** @internal */
	public postMessage(data: IMessage) {
		const invocationUid = generateUniqueHash();
		this.window.setTimeout(
			() =>
				void this.window.parent?.postMessage(
					{
						invocationUid,
						...data,
					},
					'*',
				),
		);
		return new Promise((resolve: (responseData: any) => void, reject: (error: any) => void) => {
			this.invocationResponseEmitter.once(invocationUid, (responseData: any) => {
				if (responseData.error) {
					if (responseData.error.error) {
						reject(createError(responseData.error.error as ISosErrorTransferObject));
					} else {
						reject(responseData.error);
					}
				} else {
					resolve(responseData.success);
				}
			});
		});
	}

	/**
	 * The `onReady()` method accepts an optional listener which is called after the sos API are loaded and ready. It also returns a promise
	 * which is resolved at the same time as the listener.
	 *
	 * @param listener Optional listener which is called when the sos API is ready.
	 * @return {Promise<void>} Promise which is resolved when the sos API is ready.
	 * @since 1.0.0
	 *
	 * @example
	 * sos.onReady().then(async function () {
	 *   console.log(await sos.app.getType());
	 * });
	 *
	 * sos.onReady(async function () {
	 *   console.log(await sos.app.getType());
	 * });
	 */
	public onReady(listener?: () => void): Promise<void> {
		return new Promise<void>((resolve: () => void) => {
			this.readyEmitter.once('ready', () => {
				resolve();
				if (typeof listener !== 'undefined') {
					listener();
				}
			});
			this.checkIfReady();
		});
	}

	/**
	 * The `restore()` method clears all previously played videos, streams, clear display view. The typical case of usage for digital signage
	 * is playing a loop with some specified duration. This method should be called always when the loop is changed. It will clear all
	 * previously played videos, streams, clear display views, and notify for garbage collection.
	 *
	 * It stops all video playback and clears out the memory. The following function should be triggered only in a case the whole playback
	 * needs to be restarted as it's completely switching the playback 'loop/playlist'.
	 *
	 * @example
	 * sos.onReady(async function () {
	 *   // Some function which is called when the applet is ready
	 *   sos.restore();
	 * });
	 */
	public restore(): void {
		void this.postMessage({ type: this.getMessage('restore') });
		this.removeMediaListeners();
		this.forwardRestoreToFrontAppletWindows();
	}

	/**
	 * The `refresh()` method initializes the refresh of the applet. Similar to window.location.reload(), but the applet is not downloaded again.
	 *
	 * @return {Promise<void>} Promise that is resolved when the refresh is completed.
	 * @since 1.0.0
	 *
	 * @example
	 * await sos.refresh();
	 */
	public async refresh(): Promise<void> {
		await this.postMessage({
			type: this.getMessage('applet_refresh'),
		});
	}

	private forwardRestoreToFrontAppletWindows() {
		const data = {
			type: this.messagePrefix + 'restore',
		};
		this.iframe.forwardToFrontAppletWindows(data);
	}

	private removeMediaListeners() {
		this.video.removeEventListeners();
		this.stream.removeEventListeners();
		this.sync.removeEventListeners();
		this.input.removeEventListeners();
	}

	private checkIfReady() {
		if (this.authHash !== undefined && this.config !== undefined && this.display.version !== undefined) {
			this.readyEmitter.emit('ready');
		}
	}

	private handleMessage(event: MessageEvent) {
		if (typeof event.data === 'object') {
			const source = event.source;
			if (source === this.window.parent) {
				const data = event.data;
				switch (data.type) {
					case this.getMessage('invocation.success'):
						this.invocationResponseEmitter.emit(data.invocationUid, { success: data });
						break;
					case this.getMessage('invocation.error'):
						this.invocationResponseEmitter.emit(data.invocationUid, { error: data });
						break;
					case this.getMessage('config.update'):
						// @ts-expect-error - Assigning config from external data
						this.config = data.config;
						this.checkIfReady();
						break;
					case this.getMessage('auth_hash.update'):
						// @ts-expect-error - Assigning authHash from external data
						this.authHash = data.authHash;
						this.checkIfReady();
						break;
					case this.getMessage('front_display_version.update'):
						this.display.version = data.version;
						this.checkIfReady();
						break;
					case this.getMessage('applet_version.update'):
						// @ts-expect-error - Assigning appletVersion from external data
						this.appletVersion = data.version;
						break;
					case this.getMessage('restore'):
						// TODO: shouldn't we call this.restore() instead?
						this.removeMediaListeners();
						break;
					case this.getMessage('assets.load_js'):
						void this.javascriptLoadFileController.load(data.src, this.window.document.body);
						break;
					default:
						this.browser.handleMessageData(data);
						this.sync.handleMessageData(data);
						this.video.handleMessageData(data);
						this.stream.handleMessageData(data);
						this.input.handleMessageData(data);
						this.command.handleMessageData(data);
						this.fileSystem.handleMessageData(data);
						this.sensors.handleMessageData(data);
						this.management.wifi.handleMessageData(data);
						this.hardware.handleMessageData(data);
						this.touch.handleMessageData(data);
						this.debug.handleMessageData(data);
						this.connect.handleMessageData(data);
				}
				this.iframe.forwardToFrontAppletWindows(data);
			} else if (isWindow(source) && source !== this.window) {
				const data = event.data;
				switch (data.type) {
					case this.messagePrefix + '_loader.ready':
						this.iframe.registerFrontAppletLoaderWindow(source);
						break;
					case this.messagePrefix + '_api.ready':
						this.iframe.registerBundledFrontAppletWindow(source);
						break;
					default:
						this.iframe.forwardFromFrontAppletWindows(source, data);
				}
			}
		}
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + name;
	}

	private startPinging() {
		const doPing = () => this.postMessage({ type: this.getMessage('ping') });
		this.window.setInterval(doPing, 30e3);
		void doPing();
	}
}

function isWindow(source: MessageEventSource | null): source is Window {
	return Boolean(source);
}
